// ── Media Upload for Nostr (Blossom + NIP-96 auth + fallbacks) ──────
// Tries multiple upload services with authentication.
// Supports images, videos (with automatic client-side compression), and audio.
// Services are ordered by reliability and file size limits.

import { shouldCompressVideo, compressVideo, type CompressionProgress } from './videoCompress';

// ── Upload service types ────────────────────────────────────────────

interface UploadService {
  url: string;
  name: string;
  /** Auth protocol: 'blossom' (BUD-02 kind 24242), 'nip96' (NIP-98 kind 27235), or 'none' */
  auth: 'blossom' | 'nip96' | 'none';
  /** Upload protocol: 'blossom-put' (raw body PUT), 'form' (multipart POST), 'catbox' */
  protocol: 'blossom-put' | 'form' | 'catbox';
  /** Max file size in MB */
  maxMB: number;
  /** How to extract URL from response */
  parseUrl: (data: any, raw?: string) => string | null;
  /** If true, has paid plans that raise limits */
  hasPaidPlans?: boolean;
  /** URL to upgrade page */
  upgradeUrl?: string;
}

// ── Service definitions ─────────────────────────────────────────────
//
// ORDER MATTERS — services are tried top-to-bottom.
// For videos, we prioritize services with higher limits first.
// For images, smaller reliable services are fine first.

const BLOSSOM_SERVICES: UploadService[] = [
  // nostr.build Blossom — 20MB free, up to 450MB with paid plan
  // We set maxMB to 450 and let the server reject if free tier.
  // This means paid users automatically get higher limits.
  {
    url: 'https://blossom.nostr.build/upload',
    name: 'nostr.build',
    auth: 'blossom',
    protocol: 'blossom-put',
    maxMB: 450,
    parseUrl: (data) => data?.url || data?.blob?.url,
    hasPaidPlans: true,
    upgradeUrl: 'https://nostr.build/plans/',
  },
  // blossom.primal.net — 100MB with auth
  {
    url: 'https://blossom.primal.net/upload',
    name: 'blossom.primal',
    auth: 'blossom',
    protocol: 'blossom-put',
    maxMB: 100,
    parseUrl: (data) => data?.url || data?.blob?.url,
  },
  // nosto.re — 500MB free, NIP-96 protocol
  {
    url: 'https://nosto.re/n96',
    name: 'nosto.re',
    auth: 'nip96',
    protocol: 'form',
    maxMB: 500,
    parseUrl: (data) => data?.nip94_event?.tags?.find((t: any) => t[0] === 'url')?.[1] || data?.url,
  },
  // nostr.download — up to 10GB free, NIP-96 protocol
  {
    url: 'https://nostr.download/n96',
    name: 'nostr.download',
    auth: 'nip96',
    protocol: 'form',
    maxMB: 10240,
    parseUrl: (data) => data?.nip94_event?.tags?.find((t: any) => t[0] === 'url')?.[1] || data?.url,
  },
  // nostrmedia.com — 25MB free, 100MB with paid plans
  {
    url: 'https://nostrmedia.com/upload',
    name: 'nostrmedia',
    auth: 'blossom',
    protocol: 'form',
    maxMB: 100,
    parseUrl: (data) => data?.url || data?.blob?.url || data?.nip94_event?.tags?.find((t: any) => t[0] === 'url')?.[1],
  },
  // nostr.build NIP-96 API — 20MB free, up to 450MB with paid plan
  {
    url: 'https://nostr.build/api/v2/upload/files',
    name: 'nostr.build (NIP-96)',
    auth: 'nip96',
    protocol: 'form',
    maxMB: 450,
    parseUrl: (data) => data?.data?.[0]?.url,
    hasPaidPlans: true,
    upgradeUrl: 'https://nostr.build/plans/',
  },
  // nostpic.com — 16MB, NIP-96 protocol
  {
    url: 'https://nostpic.com/api/v2/media',
    name: 'nostpic',
    auth: 'nip96',
    protocol: 'form',
    maxMB: 16,
    parseUrl: (data) => data?.nip94_event?.tags?.find((t: any) => t[0] === 'url')?.[1] || data?.url || data?.data?.link,
  },
  // catbox.moe — 200MB, no auth, but files expire after 72h (last resort)
  {
    url: 'https://litterbox.catbox.moe/resources/internals/api.php',
    name: 'catbox',
    auth: 'none',
    protocol: 'catbox',
    maxMB: 200,
    parseUrl: (_data, raw) => raw?.trim().startsWith('http') ? raw.trim() : null,
  },
];

// For images, we prefer smaller reliable services first
const IMAGE_SERVICES = BLOSSOM_SERVICES.filter(s =>
  ['nostr.build', 'blossom.primal', 'nostr.build (NIP-96)', 'nostpic', 'nosto.re', 'nostrmedia', 'catbox'].includes(s.name)
);

// For videos/audio, we prefer services with the highest limits first
const VIDEO_SERVICES = BLOSSOM_SERVICES.filter(s => s.maxMB >= 20).sort((a, b) => {
  // Prioritize by max size (biggest first), but keep nostr.build first for paid users
  if (a.name.startsWith('nostr.build') && !b.name.startsWith('nostr.build')) return -1;
  if (b.name.startsWith('nostr.build') && !a.name.startsWith('nostr.build')) return 1;
  return b.maxMB - a.maxMB;
});

// ── Size limits ─────────────────────────────────────────────────────

const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB for images
const MAX_VIDEO_SIZE = 500 * 1024 * 1024; // 500MB for videos (up from 100MB)
const MAX_AUDIO_SIZE = 50 * 1024 * 1024; // 50MB for audio

const VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska'];
const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.avi', '.mkv'];

export function isVideoFile(file: File): boolean {
  if (VIDEO_TYPES.includes(file.type)) return true;
  const ext = file.name.toLowerCase().match(/\.\w+$/)?.[0];
  return ext ? VIDEO_EXTENSIONS.includes(ext) : false;
}

const AUDIO_TYPES = ['audio/mpeg', 'audio/mp3', 'audio/ogg', 'audio/wav', 'audio/webm', 'audio/aac', 'audio/flac', 'audio/x-m4a', 'audio/mp4'];
const AUDIO_EXTENSIONS = ['.mp3', '.ogg', '.wav', '.webm', '.aac', '.flac', '.m4a', '.opus'];

export function isAudioFile(file: File): boolean {
  if (AUDIO_TYPES.some(t => file.type.startsWith(t.split('/')[0] + '/') && file.type === t)) return true;
  if (file.type.startsWith('audio/')) return true;
  const ext = file.name.toLowerCase().match(/\.\w+$/)?.[0];
  return ext ? AUDIO_EXTENSIONS.includes(ext) : false;
}

// ── Auth helpers ────────────────────────────────────────────────────

/**
 * Create a Blossom (BUD-02) authorization header for uploads.
 * Uses kind 24242 event signed by the user.
 */
async function createBlossomAuth(file: File): Promise<string | null> {
  try {
    const signer = (window as any).nostr;
    if (!signer) return null;

    const pubkey = await signer.getPublicKey();
    const now = Math.floor(Date.now() / 1000);

    // Calculate SHA-256 hash of the file for the 'x' tag
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    const authEvent = {
      kind: 24242,
      pubkey,
      created_at: now,
      tags: [
        ['t', 'upload'],
        ['x', hashHex],
        ['expiration', String(now + 300)], // 5 min expiry
      ],
      content: 'Upload media',
    };

    const signed = await signer.signEvent(authEvent);
    return 'Nostr ' + btoa(JSON.stringify(signed));
  } catch (e) {
    console.warn('[MediaUpload] Blossom auth failed:', e);
    return null;
  }
}

/**
 * Create a NIP-98 authorization header using the user's Nostr signer.
 */
async function createNIP96Auth(uploadUrl: string): Promise<string | null> {
  try {
    const signer = (window as any).nostr;
    if (!signer) return null;

    const pubkey = await signer.getPublicKey();
    const now = Math.floor(Date.now() / 1000);

    // NIP-98 requires a kind 27235 event (HTTP Auth)
    const authEvent = {
      kind: 27235,
      pubkey,
      created_at: now,
      tags: [
        ['u', uploadUrl],
        ['method', 'POST'],
      ],
      content: '',
    };

    const signed = await signer.signEvent(authEvent);
    return 'Nostr ' + btoa(JSON.stringify(signed));
  } catch (e) {
    console.warn('[MediaUpload] NIP-98 auth failed:', e);
    return null;
  }
}

// ── Core upload function ────────────────────────────────────────────

/** Errors that indicate file is too large for the service's free tier */
const SIZE_REJECTION_CODES = [413, 402, 403];

async function tryUploadToService(file: File, service: UploadService): Promise<string> {
  const headers: Record<string, string> = {};

  // Get auth header
  if (service.auth === 'blossom') {
    const auth = await createBlossomAuth(file);
    if (!auth) throw new Error('Blossom auth not available. Make sure you are logged in.');
    headers['Authorization'] = auth;
  } else if (service.auth === 'nip96') {
    const auth = await createNIP96Auth(service.url);
    if (!auth) throw new Error('NIP-98 auth not available. Make sure you are logged in.');
    headers['Authorization'] = auth;
  }

  // Execute upload based on protocol
  let response: Response;

  if (service.protocol === 'blossom-put') {
    headers['Content-Type'] = file.type || 'application/octet-stream';
    response = await fetch(service.url, { method: 'PUT', body: file, headers });
  } else if (service.protocol === 'catbox') {
    const formData = new FormData();
    formData.append('reqtype', 'fileupload');
    formData.append('time', '72h');
    formData.append('fileToUpload', file);
    response = await fetch(service.url, { method: 'POST', body: formData });
  } else {
    // form (multipart POST)
    const formData = new FormData();
    formData.append('file', file);
    response = await fetch(service.url, { method: 'POST', body: formData, headers });
  }

  if (!response.ok) {
    const text = await response.text().catch(() => '');

    // Detect paid-plan-required rejections
    if (SIZE_REJECTION_CODES.includes(response.status) && service.hasPaidPlans) {
      throw new UploadSizeError(
        `File too large for ${service.name} free tier. Upgrade for up to ${service.maxMB}MB uploads.`,
        service.name,
        service.upgradeUrl,
      );
    }

    throw new Error(`HTTP ${response.status}: ${text.slice(0, 200)}`);
  }

  // Parse response
  const raw = await response.text();
  let data: any;
  try { data = JSON.parse(raw); } catch { data = null; }

  const url = service.parseUrl(data, raw);
  if (!url) throw new Error('No URL in response');
  return url;
}

// ── Custom error for file-too-large on paid services ────────────────

export class UploadSizeError extends Error {
  serviceName: string;
  upgradeUrl?: string;

  constructor(message: string, serviceName: string, upgradeUrl?: string) {
    super(message);
    this.name = 'UploadSizeError';
    this.serviceName = serviceName;
    this.upgradeUrl = upgradeUrl;
  }
}

// ── Public upload functions ─────────────────────────────────────────

/**
 * Upload an image file and return the public URL.
 */
export async function uploadImage(
  file: File,
  onProgress?: (pct: number) => void,
): Promise<string> {
  onProgress?.(5);

  let processedFile = file;

  // Handle HEIC/HEIF from iPhones
  if (file.type === 'image/heic' || file.type === 'image/heif' || file.name.toLowerCase().endsWith('.heic')) {
    processedFile = await convertToJpeg(file);
    onProgress?.(15);
  }

  // Compress if too large (> 2MB)
  if (processedFile.size > 2 * 1024 * 1024) {
    processedFile = await compressImage(processedFile, 0.8);
    onProgress?.(25);
  }

  // Still too large? Compress more aggressively
  if (processedFile.size > MAX_IMAGE_SIZE) {
    processedFile = await compressImage(processedFile, 0.5, 1920);
    onProgress?.(35);
  }

  if (processedFile.size > MAX_IMAGE_SIZE) {
    throw new Error('Image is too large even after compression. Try a smaller image.');
  }

  onProgress?.(40);

  const fileSizeMB = processedFile.size / (1024 * 1024);
  const errors: string[] = [];
  let sizeError: UploadSizeError | null = null;

  const suitableServices = IMAGE_SERVICES.filter(s => fileSizeMB <= s.maxMB);

  for (let i = 0; i < suitableServices.length; i++) {
    const service = suitableServices[i];
    try {
      onProgress?.(40 + Math.round((i / suitableServices.length) * 55));
      const url = await tryUploadToService(processedFile, service);
      onProgress?.(100);
      return url;
    } catch (e) {
      if (e instanceof UploadSizeError) sizeError = e;
      const msg = e instanceof Error ? e.message : 'Unknown error';
      errors.push(`${service.name}: ${msg}`);
      console.warn(`[ImageUpload] ${service.name} failed:`, msg);
    }
  }

  // If we got a size error from a paid service, throw that specifically
  if (sizeError) throw sizeError;
  throw new Error(`All upload services failed:\n${errors.join('\n')}`);
}

/**
 * Upload a video file and return the public URL.
 * Automatically compresses large videos client-side before uploading.
 */
export async function uploadVideo(
  file: File,
  onProgress?: (pct: number) => void,
  onCompressionProgress?: (p: CompressionProgress) => void,
): Promise<string> {
  onProgress?.(2);

  if (file.size > MAX_VIDEO_SIZE) {
    throw new Error(`Video is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum is ${MAX_VIDEO_SIZE / 1024 / 1024}MB.`);
  }

  // ── Client-side compression ──────────────────────────────────────
  let processedFile = file;
  if (shouldCompressVideo(file)) {
    try {
      console.log(`[VideoUpload] Compressing ${(file.size / 1024 / 1024).toFixed(1)}MB video before upload...`);
      onCompressionProgress?.({ stage: 'loading', percent: 0 });
      processedFile = await compressVideo(file, onCompressionProgress);
      console.log(`[VideoUpload] Compression complete: ${(processedFile.size / 1024 / 1024).toFixed(1)}MB`);
    } catch (err) {
      // Compression failed — fall back to uploading original
      console.warn('[VideoUpload] Compression failed, uploading original:', err);
      processedFile = file;
    }
    onCompressionProgress?.({ stage: 'done', percent: 100 });
  }

  const fileSizeMB = processedFile.size / (1024 * 1024);
  onProgress?.(10);

  // Filter to services that can handle this file size
  const suitableServices = VIDEO_SERVICES.filter(s => fileSizeMB <= s.maxMB);

  if (suitableServices.length === 0) {
    throw new Error(`Video is too large (${fileSizeMB.toFixed(1)}MB) for available upload services.`);
  }

  const errors: string[] = [];
  let sizeError: UploadSizeError | null = null;

  for (let i = 0; i < suitableServices.length; i++) {
    const service = suitableServices[i];
    try {
      onProgress?.(10 + Math.round((i / suitableServices.length) * 85));
      const url = await tryUploadToService(processedFile, service);
      onProgress?.(100);
      return url;
    } catch (e) {
      if (e instanceof UploadSizeError) sizeError = e;
      const msg = e instanceof Error ? e.message : 'Unknown error';
      errors.push(`${service.name}: ${msg}`);
      console.warn(`[VideoUpload] ${service.name} failed:`, msg);
    }
  }

  // Provide a helpful message if the file was too big for free tiers
  if (sizeError) throw sizeError;
  throw new Error(`Video upload failed. Tried ${suitableServices.length} service${suitableServices.length > 1 ? 's' : ''}:\n${errors.join('\n')}`);
}

/**
 * Upload an audio file and return the public URL.
 */
export async function uploadAudio(
  file: File,
  onProgress?: (pct: number) => void,
): Promise<string> {
  onProgress?.(5);

  if (file.size > MAX_AUDIO_SIZE) {
    throw new Error(`Audio is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum is 50MB.`);
  }

  const fileSizeMB = file.size / (1024 * 1024);
  onProgress?.(10);

  const suitableServices = VIDEO_SERVICES.filter(s => fileSizeMB <= s.maxMB);
  const errors: string[] = [];

  for (let i = 0; i < suitableServices.length; i++) {
    const service = suitableServices[i];
    try {
      onProgress?.(10 + Math.round((i / suitableServices.length) * 80));
      const url = await tryUploadToService(file, service);
      onProgress?.(100);
      return url;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      errors.push(`${service.name}: ${msg}`);
    }
  }

  throw new Error(`Audio upload failed:\n${errors.join('\n')}`);
}

/**
 * Upload any media file (image, video, or audio) — auto-detects type.
 */
export async function uploadMedia(
  file: File,
  onProgress?: (pct: number) => void,
  onCompressionProgress?: (p: CompressionProgress) => void,
): Promise<{ url: string; type: 'image' | 'video' | 'audio' }> {
  if (isAudioFile(file)) {
    const url = await uploadAudio(file, onProgress);
    return { url, type: 'audio' };
  }
  if (isVideoFile(file)) {
    const url = await uploadVideo(file, onProgress, onCompressionProgress);
    return { url, type: 'video' };
  }
  const url = await uploadImage(file, onProgress);
  return { url, type: 'image' };
}

// Re-export compression types for convenience
export type { CompressionProgress } from './videoCompress';

// ── Image processing helpers ────────────────────────────────────────

/**
 * Convert HEIC/HEIF or any image to JPEG via canvas.
 */
async function convertToJpeg(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas not supported')); return; }
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(
        (blob) => {
          if (!blob) { reject(new Error('Conversion failed')); return; }
          resolve(new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' }));
        },
        'image/jpeg',
        0.9,
      );
    };
    img.onerror = () => reject(new Error('Failed to load image for conversion'));
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Compress an image to target quality and optional max dimension.
 */
async function compressImage(file: File, quality: number, maxDimension?: number): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => {
      let w = img.naturalWidth;
      let h = img.naturalHeight;

      if (maxDimension && (w > maxDimension || h > maxDimension)) {
        if (w > h) {
          h = Math.round(h * (maxDimension / w));
          w = maxDimension;
        } else {
          w = Math.round(w * (maxDimension / h));
          h = maxDimension;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas not supported')); return; }
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => {
          if (!blob) { reject(new Error('Compression failed')); return; }
          resolve(new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' }));
        },
        'image/jpeg',
        quality,
      );
    };
    img.onerror = () => reject(new Error('Failed to load image for compression'));
    img.src = URL.createObjectURL(file);
  });
}

